import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { MessageService } from '@services/helpers/message-service/message.service';
import { PaginationService } from '@services/pagination/pagination.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { ProductName } from '@models/models';
import { SNModalService } from '@web-services/modal/snModal.service';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';

@Component({
	selector: 'packages-block',
	template: require('./packages-block.component.html')
})
export class PackagesBlockComponent implements OnInit, OnDestroy {
	@Input() companyId: number;
	modalTitle: string;
	updatePackageModalId = 'updatePackageModal';
	pageRequest: any;
	packages: ProductName[] = [];
	itemsQuantity: number;
	pageNumbers: any;
	disabledBtnModal = false;
	disabledBtnDeleteModal = false;
	private allowedFilters: string[];

	readonly MODAL_TITLES = [
		'Add New Package',
		'Duplicate Package',
		'Edit Package'
	];

	readonly PACKAGE_REQUEST_DEFAULT = {
		filterBy: 'packages',
		paging: {
			pageNumber: 0,
			pageSize: 10,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	readonly PACKAGE_DEFAULT = {
		name: '',
		taxType: 'INCLUDED',
		type: 'PACKAGE',
		description: ''
	};

	selectedPackage = this.PACKAGE_DEFAULT;
	deletedPackage = this.PACKAGE_DEFAULT;
	deleteModalRef: NgbModalRef;

	constructor(
		private productPackagesService: ProductPackagesDataServiceProvider,
		private messageService: MessageService,
		private paginationService: PaginationService,
		private modalService: SNModalService
	) {}

	ngOnInit() {
		this.applyPagePackageFromLocalStorage();
		this.getPackagesData();
		this.productPackagesService.updateProductPackageSubject
			.debounceTime(500)
			.subscribe(() => {
				this.pageRequest.paging.searchToken = null;
				this.pageRequest.paging.pageNumber = 0;
				this.getPackagesData();
			});
	}

	deletePackageSubscription() {
		this.modalService
			.getModalData()
			.take(1)
			.subscribe(data => this.deletePackage(data));
	}

	ngOnDestroy() {
		this.productPackagesService.updateProductPackageSubject.complete();
	}

	private getPackagesData(): void {
		this.productPackagesService
			.getPackagesOrProducts(this.pageRequest)
			.subscribe(
				data => this.successGetPackageData(data),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successGetPackageData(data): void {
		this.packages = JSON.parse(JSON.stringify(data.items));
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pageNumbers = this.paginationService.calculatePageNumbers(
			this.itemsQuantity,
			this.pageRequest.paging.pageSize
		);
		this.pageRequest.paging.searchToken = data.searchToken;
		this.allowedFilters = data.sortingFields;
		this.setPagePackageToLocalStorage(this.pageRequest);
	}

	private findById(id): any {
		const packageItem = this.packages.filter(el => el.id === id);

		return JSON.parse(JSON.stringify(packageItem[0]));
	}

	isFilterAllowed(filterName: string) {
		if (!this.allowedFilters) {
			return false;
		}
		return this.allowedFilters.includes(filterName);
	}

	orderByField(filter: string) {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getPackagesData();
	}

	openEditPackageModal(id: number): void {
		this.selectedPackage = this.findById(id);
		this.modalTitle = this.MODAL_TITLES[2];
		$(`#${this.updatePackageModalId}`).modal('show');
	}

	openDuplicatePackageModal(id: number): void {
		this.selectedPackage = this.findById(id);
		this.modalTitle = this.MODAL_TITLES[1];
		$(`#${this.updatePackageModalId}`).modal('show');
	}

	openAddNewPackageModal(): void {
		this.selectedPackage = JSON.parse(JSON.stringify(this.PACKAGE_DEFAULT));
		this.modalTitle = this.MODAL_TITLES[0];
		$(`#${this.updatePackageModalId}`).modal('show');
	}

	openDeletePackageModal(id: number): void {
		this.deletedPackage = this.findById(id);

		this.deleteModalRef = this.modalService.openModal(DeleteItemModalComponent);

		this.deletePackageSubscription();

		this.deleteModalRef.componentInstance.modalTitle = 'Package';
		this.deleteModalRef.componentInstance.btnDisabled = false;
		this.deleteModalRef.componentInstance.modalData = this.deletedPackage;
		this.deleteModalRef.componentInstance.itemName = this.deletedPackage.name;
	}

	updatePackageData(pageRequest): void {
		this.pageRequest = pageRequest;
		this.getPackagesData();
	}

	getTaxLabel(taxType: string): string {
		let taxLabel = '';
		switch (taxType.toUpperCase()) {
			case 'INCLUDED':
				taxLabel = 'Includes';
				break;
			case 'EXCLUDED':
				taxLabel = 'Excludes';
				break;
			case 'NO_TAX':
				taxLabel = 'No';
				break;
			default:
				return '';
		}
		return taxLabel;
	}

	getModalData(modalData): void {
		this.disabledBtnModal = true;
		const { eventType, data } = modalData;
		const packageDataToSend = JSON.parse(JSON.stringify(data));

		switch (eventType) {
			case 'Add New Package':
				this.addNewPackage(packageDataToSend);
				break;
			case 'Duplicate Package':
				this.duplicatePackage(packageDataToSend);
				break;
			case 'Edit Package':
				this.updatePackage(packageDataToSend);
				break;
			default:
				break;
		}
	}

	addNewPackage(dataToSend): void {
		this.productPackagesService
			.createPackagesOrProducts(dataToSend)
			.finally(() => (this.disabledBtnModal = false))
			.subscribe(
				(data: any) => {
					this.messageService.showToaster('s_0', 'productsPackages', data.name);
					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getPackagesData();
					$(`#${this.updatePackageModalId}`).modal('hide');
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	duplicatePackage(dataToSend): void {
		this.productPackagesService
			.createPackagesOrProducts(dataToSend)
			.finally(() => (this.disabledBtnModal = false))
			.subscribe(
				(data: any) => {
					this.messageService.showToaster('s_2', 'productsPackages', data.name);
					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getPackagesData();
					$(`#${this.updatePackageModalId}`).modal('hide');
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	convertPackageToProduct(packageTemp): void {
		const body = {
			productTemplateId: packageTemp.id,
			requiredProductType: 'ADD_ON'
		};
		this.productPackagesService.convertProductPackage(body).subscribe(
			() => {
				this.productPackagesService.updateProductPackageSubject.next(
					'updateProducts'
				);
				this.messageService.showToaster(
					's_10',
					'productsPackages',
					packageTemp.name
				);
			},
			error => this.messageService.showDefaultErrorMessage(error)
		);
	}

	updatePackage(dataToSend): void {
		this.productPackagesService
			.updatePackagesOrProducts(dataToSend.id, dataToSend)
			.finally(() => (this.disabledBtnModal = false))
			.subscribe(
				(data: any) => {
					this.messageService.showToaster('s_3', 'productsPackages', data.name);
					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getPackagesData();
					$(`#${this.updatePackageModalId}`).modal('hide');
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	deletePackage(dataToSend): void {
		this.deleteModalRef.componentInstance.btnDisabled = true;
		this.productPackagesService
			.deletePackagesOrProducts(dataToSend.id)
			.finally(() => (this.disabledBtnDeleteModal = false))
			.subscribe(
				() => {
					this.messageService.showToaster(
						's_1',
						'productsPackages',
						dataToSend.name
					);
					this.pageRequest.paging.searchToken = null;
					this.pageRequest.paging.pageNumber = 0;
					this.getPackagesData();
					this.deleteModalRef.close();
				},
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	// LOCAL STORAGE

	private applyPagePackageFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`settingsPackageRequest_${this.companyId}`)
		);
		this.pageRequest = this.PACKAGE_REQUEST_DEFAULT;
		if (
			pageRequestObject !== null &&
			pageRequestObject !== undefined &&
			pageRequestObject !== ''
		) {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.sortCriterias[0].fieldName = fieldName;
			this.pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setPagePackageToLocalStorage(pageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`settingsPackageRequest_${this.companyId}`);
		localStorage.setItem(
			`settingsPackageRequest_${this.companyId}`,
			pageRequestString
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/components/packages-product/packages-block/packages-block.component.ts